import util from 'util';
import { CONSTANT } from '@const';
import {
	CertificateFilter,
	CertificateItem,
	CertificateSummary,
	CreateCertificatePayload,
	FileRetrieveResponse,
	PageDqp,
	PageDqpFilter,
	UpdateCertificatePayload,
} from '@types';

import { ApiParams, ApiService } from '../api/ApiService';
import { PageHelper } from '../helpers/PageHelper';

export class CertificateService extends ApiService {
	static readonly itemsPerPage = 20;
	protected static baseUrl = CONSTANT.DQP_API_BASE_URL;

	static async getCertificatesMock(
		pageFilter?: PageDqpFilter,
		certificateFilter?: CertificateFilter,
	): Promise<PageDqp<CertificateSummary>> {
		const allData: CertificateSummary[] = require('utils/sampleData/certificateSummaries.json'); // eslint-disable-line @typescript-eslint/no-require-imports
		let filteredData = [...allData];

		if (certificateFilter?.searchTerm) {
			const processedSearchTerm = certificateFilter.searchTerm.toLowerCase();
			filteredData = filteredData.filter(
				cert =>
					cert.meterCaseCode?.toLowerCase().includes(processedSearchTerm) ||
					cert.clientName?.toLowerCase().includes(processedSearchTerm) ||
					cert.serialNumber?.toLowerCase().includes(processedSearchTerm) ||
					cert.make?.toLowerCase().includes(processedSearchTerm) ||
					cert.model?.toLowerCase().includes(processedSearchTerm),
			);
		} else if (certificateFilter) {
			// Filter by status (mdcStatus)
			if (certificateFilter.mdcStatus) {
				const statusValues = certificateFilter.mdcStatus.split(',');
				filteredData = filteredData.filter(
					cert => cert.status && statusValues.includes(cert.status),
				);
			}

			// Filter by reason for inspection
			if (
				certificateFilter.reasonForInspection &&
				[
					CertificateSummary.reasonForInspection.NEW_METER,
					CertificateSummary.reasonForInspection.REPLACEMENT,
					CertificateSummary.reasonForInspection.REVALIDATION,
				].includes(certificateFilter.reasonForInspection as CertificateSummary.reasonForInspection)
			) {
				filteredData = filteredData.filter(
					cert => cert.reasonForInspection === certificateFilter.reasonForInspection,
				);
			}

			// Filter by inspection date range
			if (certificateFilter.inspectionFrom) {
				const fromDate = new Date(certificateFilter.inspectionFrom.toString());
				filteredData = filteredData.filter(cert => {
					if (!cert.issuedDate) return false;
					const certDate = new Date(cert.issuedDate);
					return certDate >= fromDate;
				});
			}

			if (certificateFilter.inspectionTo) {
				const toDate = new Date(certificateFilter.inspectionTo.toString());
				filteredData = filteredData.filter(cert => {
					if (!cert.issuedDate) return false;
					const certDate = new Date(cert.issuedDate);
					return certDate <= toDate;
				});
			}
		}

		return this.getPageDqpMock(filteredData, pageFilter);
	}

	static async getCertificates(
		pageFilter?: PageDqpFilter,
		certificateFilter?: CertificateFilter,
	): Promise<PageDqp<CertificateSummary>> {
		const params: ApiParams = {
			method: 'GET',
			url: CONSTANT.DQP_CERTIFICATES_API_URL,
			queryParams: { ...pageFilter, ...certificateFilter },
		};

		return await this.executeApiRequest(params).then(data => PageHelper.validateDataOrEmpty(data));
	}

	static async getCertificateDetails(certificateId: string): Promise<CertificateItem> {
		const params: ApiParams = {
			method: 'GET',
			url: util.format(`${CONSTANT.DQP_CERTIFICATE_API_URL}`, certificateId),
		};
		console.group('%cCertificateService - request to GET /Certificates/{id}', 'color: hotpink');
		console.log('%cCertificate id', 'color: cornflowerblue', certificateId);
		console.groupEnd();
		return await this.executeApiRequest(params);
	}

	static async createCertificate(payload: CreateCertificatePayload): Promise<CertificateSummary> {
		const params: ApiParams = {
			method: 'POST',
			url: CONSTANT.DQP_CERTIFICATES_API_URL,
			data: payload,
		};
		return await this.executeApiRequest(params);
	}

	static async updateCertificate(
		certificateId: string,
		certificate: UpdateCertificatePayload,
	): Promise<CertificateItem> {
		const params: ApiParams = {
			method: 'PUT',
			url: util.format(`${CONSTANT.DQP_CERTIFICATE_API_URL}`, certificateId),
			data: { ...certificate },
			headers: { itpwafexclude: CONSTANT.ITPWAF_EXCLUDE },
		};
		return await this.executeApiRequest(params);
	}

	static async deleteCertificate(certificateId: string): Promise<void> {
		const params: ApiParams = {
			method: 'DELETE',
			url: util.format(`${CONSTANT.DQP_CERTIFICATE_API_URL}`, certificateId),
		};
		return await this.executeApiRequest(params);
	}

	/**
	 * Retrieves a file from Azure Blob Storage.
	 * @param {string} url - The SAS URL for the blob container.
	 * @returns {Promise<FileRetrieveResponse>} A promise that resolves to the file retrieve response.
	 */
	static async retrieveCertificateFile(
		certificateId: string,
		fileIdentifier: string,
	): Promise<FileRetrieveResponse> {
		const params: ApiParams = {
			method: 'POST',
			url: util.format(`${CONSTANT.DQP_CERTIFICATE_FILE_API_URL}`, certificateId),
			data: { application: 'WIQM', identifier: fileIdentifier },
			headers: { itpwafexclude: CONSTANT.ITPWAF_EXCLUDE },
		};
		return await this.executeApiRequest(params);
	}
}
